import React, { useEffect, useRef, useState } from "react";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { useAlert } from "../provider/AlertProvider";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { Button, Input, InputRef, Modal, Space } from "antd";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import DataTable from "../components/DataTable";

import DetailOrder from "../components/DetailOrder";

type DataIndex = keyof Types.OrderDto;

function OrderList() {
    const alert = useAlert();

    const [orders, setOrders] = useState<Array<Types.OrderDto>>([]);
    const [currentOrder, setCurrentOrder] = useState<Types.OrderDto>();

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex, subDataIndex?: any): ColumnType<Types.OrderDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) => {
            // if (record[dataIndex] === undefined) return false;
            // const retValue = record[dataIndex]
            //     ?.toString()
            //     .toLowerCase()
            //     .includes((value as string).toLowerCase());

            // return retValue === undefined ? false : retValue;

            let fieldData = record[dataIndex];
            if (subDataIndex !== undefined) {
                const subFieldData = fieldData as any;
                fieldData = subFieldData[subDataIndex] as any;
            }

            if (fieldData === null || fieldData === undefined) return false;

            return fieldData
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.OrderDto> = [
        {
            align: "center",
            title: "ID",
            dataIndex: "orderId",
            key: "orderId",
            ...getColumnSearchProps("orderId"),
        },
        {
            align: "center",
            title: "이름",
            dataIndex: ["user", "name"],
            key: "name",
            ...getColumnSearchProps("user", "name"), // TODO:
        },
        {
            align: "center",
            title: "전화번호",
            dataIndex: ["user", "phone"],
            key: "phone",
            // ...getColumnSearchProps("user")
        },
        {
            align: "center",
            title: "모델",
            dataIndex: ["carType", "model", "modelName"],
            key: "modelName",
            // ...getColumnSearchProps("carType")
        },
        {
            align: "center",
            title: "타입",
            dataIndex: ["carType", "typeName"],
            key: "typeName",
            // ...getColumnSearchProps("carType")
        },
        {
            align: "center",
            title: "주문일자",
            dataIndex: "createdDate",
            key: "createdDate",
            render: (_, { createdDate }) => {
                if (createdDate === null || createdDate === undefined) return "-";
                return Utils.utcToLocalTime(createdDate, Common.FORMAT_DATE_TIME);
            },
        },
        {
            align: "center",
            title: "주문취소",
            dataIndex: "canceledDate",
            key: "canceledDate",
            render: (_, { canceledDate }) => {
                if (canceledDate === null || canceledDate === undefined) return "-";
                return Utils.utcToLocalTime(canceledDate, Common.FORMAT_DATE_TIME);
            },
            filters: [
                {
                    text: "주문취소 제외",
                    value: "주문취소 제외",
                },
            ],
            defaultFilteredValue: ["주문취소 제외"],
            onFilter: (value: string | number | boolean, record) => {
                if (typeof value === "number" || typeof value === "boolean") return false;

                if (record.canceledDate === null || record.canceledDate === undefined) return true;
                else return false;
            },
        },
        {
            align: "center",
            title: "예약금 결제",
            dataIndex: "payments",
            key: "payments",
            render: (_, { payments }) => {
                if (payments !== undefined && payments.length > 0) {
                    const filterValues = payments.filter((payment) => payment.paymentType === Common.PaymentType.DEPOSIT);
                    if (filterValues.length > 0 && filterValues[0].paidDate)
                        return Utils.utcToLocalTime(filterValues[0].paidDate, Common.FORMAT_DATE_TIME);
                }
                return "-";
            },

            filters: [
                {
                    text: "예약금 미결제 제외",
                    value: "예약금 미결제 제외",
                },
            ],
            defaultFilteredValue: ["예약금 미결제 제외"],
            onFilter: (value: string | number | boolean, record) => {
                if (typeof value === "number" || typeof value === "boolean") return false;

                if (record.payments !== undefined && record.payments.length > 0) {
                    const filterValues = record.payments.filter((payment) => payment.paymentType === Common.PaymentType.DEPOSIT);
                    if (filterValues.length > 0 && filterValues[0].paidDate) return true;
                }
                return false;
            },
        },
        {
            align: "center",
            title: "출고상태",
            dataIndex: "deliveryStatusDate",
            key: "deliveryStatus",
            render: (_, { deliveryStatusDate }) => {
                if (deliveryStatusDate === null || deliveryStatusDate === undefined) return "-";
                return "완료";
            },
        },
        {
            align: "center",
            title: "결제 취소",
            key: "canceledAdmin",
            render: (_, row) => {
                if ((row.canceledDate === null || row.canceledDate === undefined) && row.payments !== undefined && row.payments.length > 0) {
                    return (
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                confirmCancelOrder(row);
                            }}
                        >
                            취소
                        </Button>
                    );
                } else return "-";
            },
        },
    ];

    useEffect(() => {
        requestGetOrders();
    }, []);

    const requestAxiosGetOrders = async () => {
        let response = await utilAxiosWithAuth().get(Request.ORDER_LIST_URL);
        return response.data;
    };

    const {
        loading: loadingGetOrders,
        error: errorGetOrders,
        data: resultGetOrders,
        execute: requestGetOrders,
    } = useAsyncAxios(requestAxiosGetOrders);

    useEffect(() => {
        if (resultGetOrders === null) return;
        console.log("resultGetOrders", resultGetOrders);
        setOrders(resultGetOrders.orders);

        if (currentOrder !== undefined) {
            setCurrentOrder(resultGetOrders.orders.find((order: Types.OrderDto) => order.orderId == currentOrder.orderId));
        }
    }, [resultGetOrders]);

    useEffect(() => {
        if (errorGetOrders === null) return;

        console.log("errorGetOrders", errorGetOrders);
        alert.setAlert(errorGetOrders.response.data.code, "Error", errorGetOrders.response.data.message);
    }, [errorGetOrders]);

    const onRow = (value: Types.OrderDto) => {
        console.log("value", value);
        setCurrentOrder(value);
    };

    const confirmCancelOrder = (order: Types.OrderDto) => {
        Modal.confirm({
            title: "주문취소",
            content: "주문 취소하시겠습니까?",
            okText: "주문취소",
            onOk() {
                cancelOrder(order);
            },
            cancelText: "취소",
            onCancel() {},
            centered: true,
        });
    };

    const isPossibleCancel = (order: Types.OrderDto) => {
        const allPayments = order.payments;
        if (allPayments === undefined) return true;

        const depositPayment = Utils.getPaymentByPaymentType(Common.PaymentType.DEPOSIT, allPayments);
        if (depositPayment !== null && depositPayment.paidDate && depositPayment.paymentMethod !== "ONLINE") {
            return false;
        }

        if (Utils.isPaidByPaymentType(Common.PaymentType.DOWN_PAYMENT, allPayments) === true) {
            return false;
        }

        return true;
    };

    const checkRefundPayment = (order: Types.OrderDto) => {
        let payments: Array<Types.PaymentDto> = [];
        if (order.payments !== undefined)
            payments = order.payments.filter((payment) => payment.paidDate && payment.canceledDate === null && payment.failedDate === null);
        return payments;
    };

    const cancelOrder = (order: Types.OrderDto) => {
        const possibleCancel = isPossibleCancel(order);
        if (possibleCancel) {
            const refundPayments = checkRefundPayment(order);
            // console.log("refundPayments", refundPayments);
            if (refundPayments.length > 0) {
                requestCancelPayment(refundPayments[0]);
            } else {
                requestCancelOrder(order.orderId);
            }
        } else {
            failedPopupOrderCancel();
        }
    };

    const failedPopupOrderCancel = () => {
        Modal.error({
            title: "주문취소 실패",
            content: "이미 결제된 금액을 고객센터에 문의하여 환불 후 주문취소 해 주세요.",
            okText: "확인",
            onOk() {},
            centered: true,
        });
    };

    const successPopupCancelPayment = () => {
        Modal.info({
            title: "결제 취소",
            content: "결제가 취소되었습니다.",
            okText: "확인",
            onOk() {},
            centered: true,
        });
    };

    const errorPopupCancelPayment = () => {
        Modal.error({
            title: "결제 취소",
            content: "결제가 취소되지 않았습니다.\n다시 시도해 보시고 지속적으로 발생 할 경우\n고객센터에 문의해 주세요.",
            okText: "확인",
            onOk() {},
            centered: true,
        });
    };

    const requestAxiosCancelPayment = async (payment: Types.PaymentDto) => {
        const response = await utilAxiosWithAuth().post(Request.PAYMENT_CANCEL_URL + "/" + payment.paymentId);
        return response.data;
    };

    const {
        loading: loadingCancelPayment,
        error: errorCancelPayment,
        data: resultCancelPayment,
        execute: requestCancelPayment,
    } = useAsyncAxios(requestAxiosCancelPayment);

    useEffect(() => {
        if (resultCancelPayment === null) return;
        console.log("resultCancelPayment", resultCancelPayment);
        if (resultCancelPayment.payment.canceledDate) {
            successPopupCancelPayment();
            requestCancelOrder(resultCancelPayment.payment.order.orderId);
        } else {
            errorPopupCancelPayment();
        }
    }, [resultCancelPayment]);

    useEffect(() => {
        if (errorCancelPayment === null) return;

        console.log("errorCancelPayment", errorCancelPayment);
    }, [errorCancelPayment]);

    const requestAxiosCancelOrder = async (orderId: number) => {
        const response = await utilAxiosWithAuth().post(Request.ORDER_CANCEL_URL + "/" + orderId);
        return response.data;
    };

    const {
        loading: loadingCancelOrder,
        error: errorCancelOrder,
        data: resultCancelOrder,
        execute: requestCancelOrder,
    } = useAsyncAxios(requestAxiosCancelOrder);

    useEffect(() => {
        if (resultCancelOrder === null) return;
        console.log("resulCancelOrder", resultCancelOrder);
        requestGetOrders();
    }, [resultCancelOrder]);

    useEffect(() => {
        if (errorCancelOrder === null) return;

        console.log("errorCancelOrder", errorCancelOrder);
    }, [errorCancelOrder]);

    return (
        <>
            <DataTable hasScroll={false} columns={columns} rowKey="orderId" dataSource={orders} onRow={onRow} />
            {currentOrder && <DetailOrder order={currentOrder} requestGetOrders={requestGetOrders} />}
        </>
    );
}

export default OrderList;
